// Shared YouTube IFrame postMessage logic, extracted from the postMessage
// implementation originally authored by Paul Melero (paulmelero) in
// `GalleryNew/YoutubeVideo.vue`. Both `LiveShoppingVideo.vue` and
// `YoutubeVideo.vue` can use this composable to avoid duplicating the
// postMessage handshake, command-sending, and event-listener lifecycle.
//
// The composable is transport-only: it wires the iframe ↔ postMessage
// bridge and exposes `seekTo` + state events (`start`, `ended`,
// `progress`). The caller is responsible for the trigger model (autoplay
// vs intersection observer), the UI overlay, and any tracking calls.

import { type Ref, onUnmounted, ref, watch } from 'vue'

export const YOUTUBE_VIDEO_ORIGIN = 'https://www.youtube-nocookie.com'

// See: https://developers.google.com/youtube/iframe_api_reference#Playback_status
export const YOUTUBE_VIDEO_UNSTARTED_STATE = -1
export const YOUTUBE_VIDEO_ENDED_STATE = 0

// Buffering (3) counts as playing for our purposes — a background tab that
// was mid-buffer should still resume when the visitor comes back.
const YOUTUBE_VIDEO_PLAYING_STATES = new Set([1, 3])

export type YouTubeCommand =
  | 'playVideo'
  | 'pauseVideo'
  | 'seekTo'
  | 'mute'
  | 'unMute'
  | 'setVolume'

type YouTubePlayerStateEvent = {
  start: () => void
  ended: () => void
  progress: (percent: number) => void
}

const PROGRESS_MILESTONES = [25, 50, 75]

export function useYouTubeIframe(
  iframeRef: Ref<HTMLIFrameElement | null>,
  events: YouTubePlayerStateEvent,
) {
  const firedProgressMilestones = new Set<number>()
  // Mirrors the player's own playback state so callers can pause/resume
  // without overriding a pause the visitor made themselves.
  const isPlaying = ref(false)

  function sendCommand(command: YouTubeCommand, args: unknown[] = []) {
    iframeRef.value?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func: command, args }),
      YOUTUBE_VIDEO_ORIGIN,
    )
  }

  function seekTo(seconds: number) {
    sendCommand('seekTo', [seconds, true])
    sendCommand('playVideo')
  }

  function onMessage(event: MessageEvent) {
    if (event.origin !== YOUTUBE_VIDEO_ORIGIN) return
    if (event.source !== iframeRef.value?.contentWindow) return

    try {
      const data = JSON.parse(event.data)

      if (data?.info?.playerState === YOUTUBE_VIDEO_UNSTARTED_STATE) {
        events.start()
      }

      if (data?.info?.playerState === YOUTUBE_VIDEO_ENDED_STATE) {
        events.ended()
      }

      const { playerState } = data?.info ?? {}

      if (typeof playerState === 'number') {
        isPlaying.value = YOUTUBE_VIDEO_PLAYING_STATES.has(playerState)
      }

      const { currentTime, duration } = data?.info ?? {}

      if (
        typeof currentTime === 'number' &&
        typeof duration === 'number' &&
        duration > 0
      ) {
        const percent = (currentTime / duration) * 100

        PROGRESS_MILESTONES.forEach((milestone) => {
          if (percent >= milestone && !firedProgressMilestones.has(milestone)) {
            firedProgressMilestones.add(milestone)
            events.progress(milestone)
          }
        })
      }
    } catch {
      // Not a JSON player-state message, ignore.
    }
  }

  const stopWatchingIframe = watch(iframeRef, (iframe, _prev, onCleanup) => {
    if (!iframe) return

    window.addEventListener('message', onMessage, { passive: true })

    const notifyListening = () => {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: 'listening' }),
        YOUTUBE_VIDEO_ORIGIN,
      )
    }

    iframe.addEventListener('load', notifyListening, { passive: true })

    onCleanup(() => {
      iframe.removeEventListener('load', notifyListening)
    })
  })

  onUnmounted(() => {
    stopWatchingIframe()
    window.removeEventListener('message', onMessage)
  })

  return { seekTo, sendCommand, isPlaying }
}
